import React, { useEffect, useState } from 'react'
import { Box, Center, Image, Spinner } from '@chakra-ui/react'
import { FFLDealerMap } from "@masterffl/ffl-map-component";
import { renderToString } from 'react-dom/server';
import { formatPhoneNumber } from '../ffl-detail/utils.mjs';
import { useClient } from '@/api/backoffice-api';
import AppConfig from '@/utils/app-config.mjs';
import { getLicenseByZipDistance, getLicenseByZipQueryParams } from '../ducks/selectors';
import { Recognizer, ZipCodeRecognizer } from '@masterffl/ffl-search-component';
import FFLDetailModal from '../ffl-detail/FFLDetailModal';

interface Props {
    query: string;
    cognitoGroup: boolean;
    isSuperAdmin: boolean;
    searchRecognizer: Recognizer;
    containerHeight: string;
    isReAssignDealer?: boolean;
    onDealerAssigned?(arg: any): void;
}
  

const FFLMapView = ({
    query,
    cognitoGroup,
    isSuperAdmin,
    searchRecognizer,
    containerHeight,
    isReAssignDealer,
    onDealerAssigned,
  }: Props) => {

    const [mapPageNumber, setMapPageNumber] = useState(1);
    const [dealersForMap, setDealersForMap] = useState<any[]>([]);
    const [licenseId, setLicenseId] = useState('')
    const getLicenseByZipParams = getLicenseByZipQueryParams();
    const getDistance = getLicenseByZipDistance();

    const [{ data: mapData, loading: mapDataLoading, error: mapDataError }] = useClient({
        baseURL: AppConfig.fflGetApiBaseURL,
        url: `/v2/licenses`,
        timeout: 60000,
        params: {
            ...getLicenseByZipParams,
            types: getLicenseByZipParams?.types,
            ...(searchRecognizer instanceof ZipCodeRecognizer ? { zipCode: query, distance: getDistance } : { business_name: query }),
            page_size: 20,
            page_number: mapPageNumber
        }
    });

    useEffect(() => {
        if (mapData && mapData.meta && mapData.meta.hasMore && !mapDataLoading) {
            setMapPageNumber(mapPageNumber + 1);
        }
        if (mapData && mapData.dealers && mapData.dealers.length > 0 && !mapDataLoading) {
            setDealersForMap((prevDealers) => [...prevDealers, ...mapData.dealers]);
        }
    }, [mapData, mapDataLoading])

    useEffect(() => {

        setDealersForMap([])
        setMapPageNumber(1)
    }, [getDistance, getLicenseByZipParams, query, mapDataError]);


    const getAddress = (address: any) => {
        return `${address?.street1}, ${address?.city}, ${address?.state}, ${address?.zip}`;
    };

    const getLinks = (dealer: any) => {
        return (
            <>
                {dealer?.contact?.primaryPhone && (
                    <a
                        href={`tel:${dealer.contact.primaryPhone}`}
                        title={formatPhoneNumber(dealer.contact.primaryPhone) as string}
                    >
                        <img src="/images/ui/phone.svg" width="30" height="30" />
                    </a>
                )}
                {dealer.placeDetails?.google &&
                    dealer.placeDetails?.google.profile_url && (
                        <a href={dealer.placeDetails?.google.profile_url} target="blank">
                            <img src="/images/ui/google.svg" width="30" height="30" />
                        </a>
                    )}
                {dealer.placeDetails?.facebook &&
                    dealer.placeDetails.facebook.profile_url?.length && (
                        <a href={dealer.placeDetails.facebook.profile_url} target="blank">
                            <img src="/images/ui/facebook.svg" width="30" height="30" />
                        </a>
                    )}
                {dealer.placeDetails?.yelp &&
                    dealer.placeDetails.yelp.profile_url?.length && (
                        <a href={dealer.placeDetails.yelp.profile_url} target="blank">
                            <img src="/images/ui/yelp.svg" width="30" height="30" />
                        </a>
                    )}
            </>
        );
    };

    const createDealerCard = (dealer: any) => {
        const links = renderToString(getLinks(dealer));
        return `<div class="info-container">
                    <p class="name-label">${dealer?.name}</p>
                    <p class="address-label">${getAddress(dealer?.contact?.address)}</p>
                    <div class="review-row mt-2">
                        <img src="/images/ui/star.svg" />
                        <p class="decs-text mt-1">${dealer?.socialRating?.score ? dealer?.socialRating?.score : 'No ratings'}</p>
                        ${dealer?.socialRating?.count
                ? `<span class="gray-text">(${dealer?.socialRating?.count} reviews)</span>`
                : ''
            }
                    </div>
                    <div class="review-row space-between mt-3 mb-2">
                          <div class="review-row">
                             ${dealer?.businessType ? `<img src="/images/ui/briefcase.svg" /> <p class="decs-text">${dealer?.businessType}</p>` : ''}
                          </div>
                          <div class="info-container__icons">
                          ${dealer?.isRange ?  '<img src="/images/ui/shooting-range.svg" title="Shooting range" />' : ""}
                          ${dealer?.deliveryIndicator ? `
                            <img src="/images/ui/${dealer?.deliveryIndicator}.svg" />
                            ` : ''}
                          </div>
                    </div>
                    <div class="review-row mt-2 align-end">
                        <div class="social-links">
                          ${links}
                        </div>
                        <div class="action-col">
                            <button id="info-view-btn" class="map__primary-btn mb-1" data-value="${dealer.id}">${isReAssignDealer && onDealerAssigned ? "Select" : "View Details"}</button>
                            ${dealer?.geo?.distance ? `
                            <div class="review-row">
                                <img src="/images/ui/location.svg" />
                                <p class="decs-text miles-text mr-0">${(dealer?.geo?.distance).toFixed(1)} miles from center</p></div>
                                `
                : ''}
                        </div>
                    </div>
                </div>`;
    }

    const onNavigateDetails = (id: string) => {
        setLicenseId(id)
    }

    const onDetailsModelClose = () => {
        setLicenseId('')
    }

    const noDataComponent = () => {
        return (
            <Box className="not-found-box d-no-data">
                <Image src='/images/ui/not-found-icon.png' alt="no data" />
                <>
                    <p className="not-found-box__title">No FFL Dealers found</p>
                    <p className="not-found-box__message">
                        Try adjusting your search or filter to find what you’re looking for
                    </p>
                </>
            </Box>
        )
    }

    return (
        <>
            {dealersForMap && dealersForMap.length > 0 ?
                <Box className='map-container'>
                    <FFLDealerMap
                        mapKey={AppConfig.mapAPIKey}
                        dealerList={dealersForMap}
                        getDealerInfoCardHtml={createDealerCard}
                        cardBtnClickHandler={onNavigateDetails}
                        height={containerHeight ? containerHeight : 'calc(100vh - 253px)'}
                    />
                </Box>
                : !dealersForMap.length && mapDataLoading && !mapDataError ? (
                    <Center height={'calc(100vh - 253px)'}>
                        <Spinner />
                    </Center>
                ) : !dealersForMap.length && !mapDataLoading && noDataComponent()
            }
            <FFLDetailModal
                id={licenseId}
                isOpen={licenseId ? true : false}
                onClose={onDetailsModelClose}
                cognitoGroup={cognitoGroup}
                isSuperAdmin={isSuperAdmin}
            />
        </>
    )
}

export default FFLMapView
